// src/components/PhotoTaskCard.jsx
import React, { useRef } from 'react';
import BiometricDiagram from './BiometricDiagram';
import './PhotoTaskCard.css';

// Professional SVG Rule Icons
function RuleIcon({ type }) {
  if (type === 'prohibit') {
    return (
      <span className="rule-icon-badge prohibit" title="Prohibited">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10" />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
      </span>
    );
  }
  if (type === 'light') {
    return (
      <span className="rule-icon-badge light" title="Illumination">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
        </svg>
      </span>
    );
  }
  if (type === 'gaze' || type === 'look') {
    return (
      <span className="rule-icon-badge gaze" title="Eye Alignment">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </span>
    );
  }
  if (type === 'background') {
    return (
      <span className="rule-icon-badge bg" title="Background Diversity">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      </span>
    );
  }
  if (type === 'pitch' || type === 'yaw' || type === 'angle') {
    return (
      <span className="rule-icon-badge angle" title="Angle Orientation">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10" />
          <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
        </svg>
      </span>
    );
  }

  // Default Standard Check
  return (
    <span className="rule-icon-badge standard" title="Requirement">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

export default function PhotoTaskCard({
  task,
  photo,
  status = 'READY', // 'LOCKED' | 'READY' | 'COOLDOWN' | 'ACCEPTED'
  cooldownSeconds = 0,
  examplePhotoUrl = null,
  onCapture,
  onUpload,
  onRetake,
  onNext,
  hasNext = false,
  totalTasks = 5,
  currentIndex = 0
}) {
  // eslint-disable-next-line no-unused-vars
  const fileInputRef = useRef(null);

  const isAccepted = status === 'ACCEPTED' || (photo && (photo.is_valid || photo.validation_results?.is_valid));
  const isCooldown = status === 'COOLDOWN' || cooldownSeconds > 0;
  const isLocked = status === 'LOCKED';

  // eslint-disable-next-line no-unused-vars
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(file, task);
    }
    e.target.value = '';
  };

  return (
    <div className={`photo-task-card ${isAccepted ? 'is-accepted' : ''} ${isCooldown ? 'is-cooldown' : ''}`}>
      {/* 1. Technical Card Header */}
      <div className="task-card-header">
        <div className="task-header-left">
          <span className="task-step-pill">
            {task.stage === 'Present_Neutral' ? `Selfie ${currentIndex + 1} of ${totalTasks}` : `Pose ${task.number}`}
          </span>
          <h3 className="task-title">{task.title}</h3>
        </div>

        <div className="task-header-right">
          {isAccepted ? (
            <span className="task-status-badge accepted">
              <span className="status-dot green" />
              <span>Verified & Accepted</span>
            </span>
          ) : isCooldown ? (
            <span className="task-status-badge cooldown">
              <span className="status-dot amber" />
              <span>Cooldown: {cooldownSeconds}s</span>
            </span>
          ) : isLocked ? (
            <span className="task-status-badge locked">
              <span>Locked</span>
            </span>
          ) : (
            <span className="task-status-badge ready">
              <span className="status-dot blue" />
              <span>Ready for Capture</span>
            </span>
          )}
        </div>
      </div>

      {/* 2. Main Card Body */}
      <div className="task-card-body">
        {/* Headline & Guidance Caption */}
        <div className="task-headline-row">
          <h4 className="task-headline">{task.headline}</h4>
          <p className="task-caption">{task.caption}</p>
        </div>

        {/* 3. Professional Biometric Visual Reference Container */}
        <div className="task-example-container">
          {examplePhotoUrl ? (
            <div className="example-photo-wrapper">
              <span className="task-example-tag">Verified Photo Sample</span>
              <img
                src={examplePhotoUrl}
                alt={`Example for ${task.title}`}
                className="task-example-img"
              />
            </div>
          ) : (
            <BiometricDiagram task={task} />
          )}

          {task.targetPitch && (
            <div className="task-target-angle-badge">
              <span>📐 Pitch: {task.targetPitch}</span>
              <span>• Yaw: {task.targetYaw}</span>
            </div>
          )}
        </div>

        {/* 4. Verification Standards Checklist */}
        <div className="task-rules-box">
          <div className="task-rules-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Capture Specifications & Requirements</span>
          </div>
          <ul className="task-rules-list">
            {(task.rules || []).map((rule, idx) => (
              <li key={idx} className="task-rule-item">
                <RuleIcon type={rule.type} />
                <span className="rule-text">{rule.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 5. Biometric Advisory & Cooldown Section */}
        <div className={`task-clause-box ${isCooldown ? 'cooldown-active' : ''}`}>
          <div className="clause-icon-wrap">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <p className="clause-text">
              {isCooldown ? (
                <strong>Cooldown active: Please move to a different room or background. Unlocking next shot in {cooldownSeconds} seconds...</strong>
              ) : (
                task.clause || 'Ensure balanced lighting and keep face within the oval boundary.'
              )}
            </p>
            {isCooldown && (
              <div className="cooldown-timer-bar">
                <div
                  className="cooldown-timer-fill"
                  style={{ width: `${Math.min(100, Math.max(0, (cooldownSeconds / 60) * 100))}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* 6. Accepted State View or Action Buttons */}
        {isAccepted && photo ? (
          <div className="task-accepted-preview">
            <div className="accepted-thumb-wrapper">
              <img
                src={photo.preview_url || photo.url || (photo.id ? `/api/photos/${photo.id}/file` : '')}
                alt="Captured validation"
                className="accepted-thumb-img"
              />
              <span className="accepted-thumb-badge">✓</span>
            </div>
            <div className="accepted-info">
              <h5 className="accepted-title">✓ Verified & Accepted</h5>
              <p className="accepted-desc">This capture passes all biometric validation rules and quality criteria.</p>
              <div className="accepted-actions">
                <button
                  type="button"
                  className="btn-retake"
                  onClick={() => onRetake && onRetake(photo, task)}
                >
                  Retake Photo
                </button>
                {hasNext && onNext && (
                  <button
                    type="button"
                    className="btn-retake"
                    style={{ background: '#2563eb', color: '#ffffff', borderColor: '#2563eb' }}
                    onClick={onNext}
                  >
                    Next Photo ➔
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="task-actions-grid" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', margin: '1.25rem auto 0 auto' }}>
            {/* Primary Live Camera Button */}
            <button
              type="button"
              className="btn-action-primary"
              disabled={isLocked || isCooldown}
              onClick={() => onCapture && onCapture(task)}
              style={{ margin: '0 auto', maxWidth: '380px', width: '100%', justifyContent: 'center' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ marginRight: '0.4rem' }}>
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <span>{isCooldown ? `Wait ${cooldownSeconds}s` : 'Capture with Camera'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
